import { useEffect, useState } from "react";
import { Plus, Search, Pencil, Trash2, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatINR, formatDate } from "@/lib/format";
import { getRange, rangeParams } from "@/lib/period";
import PeriodFilter from "@/components/PeriodFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function LedgerPage({ type }) {
  const isRev = type === "revenue";
  const { version, refresh, openTransaction } = useApp();
  const [periodKey, setPeriodKey] = useState("this_year");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [range, setRange] = useState(getRange("this_year"));
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [paymentMode, setPaymentMode] = useState("all");
  const [sort, setSort] = useState("newest");
  const [categories, setCategories] = useState([]);
  const [toDelete, setToDelete] = useState(null);

  const catField = isRev ? "revenue_type" : "category";
  const base = isRev ? "/revenues" : "/expenses";
  const catEndpoint = isRev ? "/revenue-categories" : "/expense-categories";

  useEffect(() => { api.get(catEndpoint).then((r) => setCategories(r.data.filter((c) => c.is_active))); }, [catEndpoint]);

  const load = () => {
    const params = { ...rangeParams(range), sort };
    if (q) params.q = q;
    if (category !== "all") params[isRev ? "revenue_type" : "category"] = category;
    if (paymentMode !== "all") params.payment_mode = paymentMode;
    api.get(base, { params }).then((r) => setRows(r.data));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range, sort, category, paymentMode, version]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const confirmDelete = async () => {
    try {
      await api.delete(`${base}/${toDelete.id}`);
      toast.success(`${isRev ? "Revenue" : "Expense"} deleted`);
      setToDelete(null);
      refresh();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid={`${type}-page`}>
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">{isRev ? "Revenue" : "Expenses"}</h1>
          <p className="mt-1 text-slate-500">{rows.length} transactions · {formatINR(total)} total</p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodFilter periodKey={periodKey} setPeriodKey={setPeriodKey} custom={custom} setCustom={setCustom} onChange={setRange} />
          <Button onClick={() => openTransaction(type)} className="gap-2 bg-orange-600 hover:bg-orange-700" data-testid={`add-${type}-btn`}>
            <Plus className="h-4 w-4" /> Add {isRev ? "Revenue" : "Expense"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder={`Search ${isRev ? "description / reference" : "description / vendor / invoice"}`} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="search-input" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[190px]" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All {isRev ? "Types" : "Categories"}</SelectItem>
            {categories.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-[170px]" data-testid="filter-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="highest">Highest amount</SelectItem>
            <SelectItem value="lowest">Lowest amount</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">{isRev ? "Type" : "Category"}</th>
                <th className="px-5 py-3">Description</th>
                <th className="px-5 py-3">{isRev ? "Reference" : "Vendor"}</th>
                <th className="px-5 py-3">Payment</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400">No {type} transactions found</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 transition-colors hover:bg-slate-50" data-testid={`row-${r.id}`}>
                  <td className="px-5 py-3 text-slate-600">{formatDate(r.date)}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${isRev ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                      {isRev ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{r[catField]}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-500">{r.description}</td>
                  <td className="px-5 py-3 text-slate-500">{isRev ? r.reference_number : r.vendor}</td>
                  <td className="px-5 py-3 text-slate-500">{r.payment_mode}</td>
                  <td className={`px-5 py-3 text-right font-semibold ${isRev ? "text-emerald-600" : "text-rose-600"}`}>{formatINR(r.amount)}</td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openTransaction(type, r)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600" data-testid={`edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => setToDelete(r)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600" data-testid={`delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete transaction?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this transaction? This can be undone by an administrator.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
